
/**
 * Response error
 */
export class ResponseError extends Error {

  /**
   * Constructor
   */
  constructor(response, config, data) {

    //Create message
    const {status} = response
    const message = ResponseError.getErrorMessage(data, status)

    //Parent constructor
    super(message)

    //Set props
    this.name = this.constructor.name
    this.response = response
    this.config = config
    this.status = status
    this.data = data
    this.message = message

    //Capture stack trace
    if (!this.stack) {
      Error.captureStackTrace(this, this.constructor)
    }
  }

  //Ignore server errors in Sentry unless we're not expecting them
  get ignoreInSentry() {
    return (
      this.isServerError ||
      this.isNetworkError ||
      this.isUnauthenticatedError ||
      this.isUnauthorizedError
    )
  }

  //Error type helpers
  get isResponseError() {
    return true
  }
  get isClientError() {
    return this.status >= 400 && this.status <= 499
  }
  get isServerError() {
    return this.status >= 500 && this.status <= 599
  }
  get isNetworkError() {
    return this.status === 0 || this.status === -1
  }
  get isBadRequestError() {
    return this.status === 400
  }
  get isUnauthenticatedError() {
    return this.status === 401
  }
  get isUnauthorizedError() {
    return this.status === 403
  }
  get isNotFoundError() {
    return this.status === 404
  }
  get isRequestTimeoutError() {
    return this.status === 408
  }
  get isConflictError() {
    return this.status === 409
  }
  get isValidationError() {
    return this.status === 422
  }
  get isTooManyRequestsError() {
    return this.status === 429
  }
  get isInternalServerError() {
    return this.status === 500
  }
  get isNotImplementedError() {
    return this.status === 501
  }
  get isGatewayError() {
    return this.status === 502
  }
  get isServiceUnavailableError() {
    return this.status === 503
  }
  get isGatewayTimeoutError() {
    return this.status === 504
  }

  /**
   * Error message generator
   */
  static getErrorMessage(data, status) {

    //Check for message or error in data
    if (data?.message) {
      return data.message
    }
    if (data?.error) {
      return data.error
    }

    //Status-specific message
    if (status === 400) {
      return 'Bad request'
    }
    if (status === 401) {
      return 'Unauthenticated'
    }
    if (status === 403) {
      return 'Unauthorized'
    }
    if (status === 404) {
      return 'Not found'
    }
    if (status === 408) {
      return 'Request timeout'
    }
    if (status === 409) {
      return 'Conflict'
    }
    if (status === 422) {
      return 'Validation error'
    }
    if (status === 429) {
      return 'Too many requests'
    }
    if (status === 500) {
      return 'Internal server error'
    }
    if (status === 501) {
      return 'Not implemented'
    }
    if (status === 502) {
      return 'Bad gateway'
    }
    if (status === 503) {
      return 'Service unavailable'
    }
    if (status === 504) {
      return 'Gateway timeout'
    }
    if (status >= 400 && status <= 499) {
      return `Client error (${status})`
    }
    if (status >= 500 && status <= 599) {
      return `Server error (${status})`
    }
    if (status === 0 || status === -1) {
      return 'Network error'
    }
    return `Response error (${status})`
  }
}
